import type { DomainCrawlerResponse, IncidentDomainResponse } from '@prerender/diagnostics-api';
import { Fragment, useId, useState } from 'react';
import { Table } from 'react-bootstrap';

import { LastMeasuredNote, NUMERIC_CELL } from './CrawlerEvidence';
import { formatDropAsChange, formatRequestCount } from './crawlerEvidenceFigures';
import { crawlerLabel } from './crawlerLabels';
import DomainCrawlerTable from './DomainCrawlerTable';
import { ariaSortOf, type DomainSort, type DomainSortKey, matchesSearch, nextSort, sortedBy } from './domainSort';
import { domainStateLabel } from './domainStateLabels';
import EvidenceTable from './EvidenceTable';
import DetailField from './DetailField';
import { formatDayAndMonthWithOtherYear, formatDetectedAt } from './formatDetectedAt';
import { formatRate } from './formatMeasuredValue';
import { metricSentence } from './metricSentence';
import OriginBlockingDomainFigures from './OriginBlockingDomainFigures';
import {
  blockingFiguresOf,
  formatBlockedOfTotal,
  isRecovered,
  ORIGIN_BLOCKING_INCIDENT_TYPE,
  type RateRules,
} from './originBlockingFigures';
import SeverityTag from './SeverityTag';

type IncidentDomainsProps = {
  domains: readonly IncidentDomainResponse[];
  incidentType: string;
  rules: RateRules | null;
};

const TITLE = 'Domains';
const CRAWLERS_COLUMN = 'Crawlers';

type DomainColumn = {
  readonly label: string;
  readonly sortKey?: DomainSortKey;
  readonly numeric?: boolean;
};

const BLOCKING_COLUMNS: readonly DomainColumn[] = [
  { label: 'Blocked request rate', numeric: true },
  { label: 'Affected URLs', numeric: true },
];

function columnsOf(listsCrawlers: boolean, showsBlocking: boolean): DomainColumn[] {
  return [
    { label: '' },
    { label: 'Domain', sortKey: 'domain' },
    ...(listsCrawlers ? [{ label: CRAWLERS_COLUMN }] : []),
    ...(showsBlocking ? BLOCKING_COLUMNS : []),
    { label: 'Severity', sortKey: 'severity' },
  ];
}

function BlockingCells({ row }: { row: IncidentDomainResponse }) {
  const figures = blockingFiguresOf(row.evidence);

  return (
    <>
      <td className={NUMERIC_CELL}>
        <div className="diagnostics-domain-figure">{formatRate(row.metricValue)}</div>
        {figures !== null && (
          <div className="diagnostics-evidence-note">
            {`${formatBlockedOfTotal(figures)} crawler requests`}
          </div>
        )}
        {isRecovered(row) && <LastMeasuredNote />}
      </td>
      <td className={NUMERIC_CELL}>
        <div className="diagnostics-domain-figure">{formatRequestCount(row.affectedUrlCount)}</div>
        <div className="diagnostics-evidence-note">unique URLs</div>
      </td>
    </>
  );
}

type DomainCrawlersProps = {
  domain: string;
  crawlers: readonly DomainCrawlerResponse[];
};

function DomainCrawlers({ domain, crawlers }: DomainCrawlersProps) {
  return (
    <ul className="diagnostics-domain-crawlers" aria-label={`Crawlers on ${domain}`}>
      {crawlers.map(({ crawler, metricValue, noLongerAffected }) => (
        <li key={crawler} className={noLongerAffected ? 'diagnostics-evidence-no-longer-affected' : undefined}>
          {crawlerLabel(crawler)} <strong>{formatDropAsChange(metricValue)}</strong>
          {noLongerAffected && ' no longer affected'}
        </li>
      ))}
    </ul>
  );
}

function DomainStateTag({ state, noLongerAffectedSince }: Pick<IncidentDomainResponse, 'state' | 'noLongerAffectedSince'>) {
  if (state === 'affected') return null;

  const since =
    noLongerAffectedSince === null
      ? ''
      : ` since ${formatDayAndMonthWithOtherYear(new Date(noLongerAffectedSince), new Date())}`;

  return <span className="diagnostics-domain-tag diagnostics-domain-state-tag">{`${domainStateLabel(state)}${since}`}</span>;
}

function DomainFigures({ domain }: { domain: IncidentDomainResponse }) {
  return (
    <dl className="diagnostics-domain-figures">
      <DetailField name="Metric">{metricSentence(domain.metricLabel, domain.metricValue)}</DetailField>
      <DetailField name="Affected URLs">{domain.affectedUrlCount}</DetailField>
      <DetailField name="First detected">{formatDetectedAt(new Date(domain.firstDetectedAt))}</DetailField>
      <DetailField name="Last detected">{formatDetectedAt(new Date(domain.lastDetectedAt))}</DetailField>
    </dl>
  );
}

type ExpandedDomainProps = {
  row: IncidentDomainResponse;
  showsBlocking: boolean;
  rules: RateRules | null;
};

function ExpandedDomain({ row, showsBlocking, rules }: ExpandedDomainProps) {
  if (row.crawlers.length > 0) {
    return <DomainCrawlerTable domain={row.domain} crawlers={row.crawlers} />;
  }

  const figures = showsBlocking ? blockingFiguresOf(row.evidence) : null;
  if (figures !== null) {
    return <OriginBlockingDomainFigures domain={row} figures={figures} rules={rules} />;
  }

  return (
    <>
      <DomainFigures domain={row} />
      <EvidenceTable evidence={row.evidence} name={`Evidence for ${row.domain}`} />
    </>
  );
}

function IncidentDomains({ domains, incidentType, rules }: IncidentDomainsProps) {
  const titleId = useId();
  const listsCrawlers = domains.some(({ crawlers }) => crawlers.length > 0);
  const showsBlocking = incidentType === ORIGIN_BLOCKING_INCIDENT_TYPE;
  const columns = columnsOf(listsCrawlers, showsBlocking);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<DomainSort | null>(null);
  const listed = sortedBy(
    domains.filter(({ domain }) => matchesSearch(domain, search)),
    sort
  );

  const toggle = (domain: string): void => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(domain)) next.add(domain);

      return next;
    });
  };

  return (
    <section className="diagnostics-detail-domains" aria-labelledby={titleId}>
      <h2 id={titleId} className="diagnostics-detail-section-title">
        {TITLE} ({domains.length})
      </h2>
      <span className="diagnostics-domain-search">
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
          <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          placeholder="Search domains"
          aria-label="Search domains"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </span>
      <Table className="diagnostics-table diagnostics-domains-table" aria-label={TITLE}>
        <thead>
          <tr>
            {columns.map(({ label, sortKey, numeric }) => (
              <th
                key={label}
                scope="col"
                className={numeric ? NUMERIC_CELL : undefined}
                aria-sort={sortKey === undefined ? undefined : ariaSortOf(sort, sortKey)}
              >
                {sortKey === undefined ? (
                  label
                ) : (
                  <button
                    type="button"
                    className="diagnostics-sort-button"
                    onClick={() => setSort((current) => nextSort(current, sortKey))}
                  >
                    {label}
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {listed.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="diagnostics-domain-search-empty">
                No domains match &apos;{search.trim()}&apos;
              </td>
            </tr>
          )}
          {listed.map((row) => {
            const open = expanded.has(row.domain);

            return (
              <Fragment key={row.domain}>
                <tr>
                  <td className="diagnostics-domain-toggle-cell">
                    <button
                      type="button"
                      className="diagnostics-domain-toggle"
                      aria-expanded={open}
                      aria-label={`${open ? 'Hide' : 'Show'} details for ${row.domain}`}
                      onClick={() => toggle(row.domain)}
                    />
                  </td>
                  <th scope="row">
                    {row.domain}
                    {(row.state !== 'affected' || row.joinedAt != null) && (
                      <span className="diagnostics-domain-tags">
                        <DomainStateTag state={row.state} noLongerAffectedSince={row.noLongerAffectedSince} />
                        {row.joinedAt != null && (
                          <span className="diagnostics-domain-tag diagnostics-joined-badge">
                            Joined {formatDayAndMonthWithOtherYear(new Date(row.joinedAt), new Date())}
                          </span>
                        )}
                      </span>
                    )}
                  </th>
                  {listsCrawlers && (
                    <td>
                      <DomainCrawlers domain={row.domain} crawlers={row.crawlers} />
                    </td>
                  )}
                  {showsBlocking && <BlockingCells row={row} />}
                  <td>
                    <SeverityTag severity={row.severity} />
                  </td>
                </tr>
                {open && (
                  <tr className="diagnostics-domain-expansion">
                    <td colSpan={columns.length}>
                      <ExpandedDomain row={row} showsBlocking={showsBlocking} rules={rules} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
    </section>
  );
}

export default IncidentDomains;
